import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { overrideSchema, topPrsSchema } from "./schema";

const overrideExample = {
  profile: {
    name: "John Connor",
    profile_image_url: "https://img.freepik.com/free-psd/3d-render-avatar-character_23-2150611737.jpg",
    slug: "john-connor-url-slug",
    title: "Savior of the world",
    company_name: "Future Inc.",
    company_slug: "future-inc-url-slug",
  },
  pitch_speed_points_score: 10,
  direct_media_reply_points_score: 20,
  conversation_outcome_points_score: 10,
};

// CodeEditor: Allows editing JSON-like top PR data with validation and
// formatting
function CodeEditor({ topPrs, onTopPrsChanged }) {
  const [code, setCode] = useState(""); // Current editor content
  const [originalCode, setOriginalCode] = useState(""); // Original content for reset
  const [error, setError] = useState(null); // Validation errors
  const [hasChanges, setHasChanges] = useState(false); // Tracks unsaved changes

  // Make sure we'll be presenting a proper example
  overrideSchema.validateSync(overrideExample, { strict: true });

  // Sync editor content with incoming topPrs prop
  useEffect(() => {
    const topPrsJson = JSON.stringify(topPrs, null, 2);
    setCode(topPrsJson);
    setOriginalCode(topPrsJson);
  }, [topPrs]);

  // Handle user input in the editor
  const handleCodeChange = (event) => {
    setCode(event.target.value);
    setHasChanges(event.target.value !== originalCode);
  };

  // Validate JSON and notify parent component of changes
  const handleValidate = async () => {
    try {
      const newTopPrs = JSON.parse(code);
      await topPrsSchema.validate(newTopPrs, { strict: true });
      setError(null);
      onTopPrsChanged(newTopPrs);

      // Format JSON and reset state after successful validation
      const prettyCode = JSON.stringify(newTopPrs, null, 2);
      setOriginalCode(prettyCode);
      setCode(prettyCode);
      setHasChanges(false);
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
    }
  };

  // Reset editor to the original content
  const handleCancel = () => {
    setCode(originalCode);
    setError(null);
    setHasChanges(false);
  };

  return (
    <div>
      <div>
        {/* Textarea for JSON input */}
        <textarea className="form-control" rows="20" value={code} onChange={handleCodeChange} />
        {/* Error message */}
        {error && <div className="text-danger mt-2">{error}</div>}
        {/* Action buttons for validation and reset */}
        {hasChanges && (
          <div className="mt-3">
            <button type="button" className="btn btn-primary me-2" onClick={handleValidate}>
              Validate &amp; save
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        )}
      </div>
      {topPrs.length > 0 && (
        <div>
          <h2 className="mt-4">Overrides</h2>
          <p>
            User profile details are fetched on-demand to keep Qwoted 100 updated with the latest information, like
            name, avatar, and company changes, without manual effort.
          </p>
          <p>Scores used to render bars are calculated on the backend to fit within a visually appealing range.</p>{" "}
          <p>
            While this is usually convenient, it can occasionally pose challenges, such as missing profile data or the
            need for last-minute score adjustments.
          </p>
          <p>
            To address this, overrides can be applied to any Qwoted 100 entry. Below is an example of an override,
            allowing you to replace any rendered field. Fields can be omitted as needed.
          </p>
          <pre>{JSON.stringify({ override: overrideExample }, null, 2)}</pre>
          <p>
            It should live among sibling keys like <code>profile_id</code> and <code>profile_slug</code>, for example:
          </p>
          <pre>{JSON.stringify({ ...topPrs[0], override: overrideExample }, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

CodeEditor.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  topPrs: PropTypes.array.isRequired, // Array of top PR data
  onTopPrsChanged: PropTypes.func.isRequired, // Callback to update parent state
};

export default CodeEditor;
